/**
 * Bundled in-tree plugins (`src/Plugins/<slug>/manifest.tsx`, registered by
 * the seam in `vite.config.mts`) are part of this build and required for it to
 * be the intended application. CARE Suriname core patch; see
 * docs/core-patches.md, "bundled plugins".
 */
export type BundledPluginState = "loading" | "failed" | "ready";

// Structural subset of PluginEngine's per-plugin query result.
export interface BundledPluginCandidate {
  isLoading: boolean;
  isError?: boolean;
  plugin?: unknown;
  meta?: unknown;
}

const packageOf = (meta: unknown) =>
  meta && typeof meta === "object"
    ? (meta as { package?: unknown }).package
    : undefined;

export const isBundledPlugin = (plugin: BundledPluginCandidate) => {
  const pkg = packageOf(plugin.meta);
  return typeof pkg === "string" && pkg.startsWith("bundled/");
};

/**
 * `loading` while any bundled manifest is still being imported, `failed` when
 * one errored or resolved without a manifest, `ready` otherwise. Federated
 * remotes and API-configured plugins are never considered: upstream lets those
 * fail open, and this must not turn a third-party outage into a blank screen.
 */
export function getBundledPluginState(
  plugins: readonly BundledPluginCandidate[],
): BundledPluginState {
  const bundled = plugins.filter(isBundledPlugin);
  if (bundled.some((plugin) => plugin.isLoading)) {
    return "loading";
  }
  if (
    bundled.some(
      (plugin) => plugin.isError || typeof plugin.plugin !== "string",
    )
  ) {
    return "failed";
  }
  return "ready";
}
